'use client';

import ace from 'ace-code';
import 'ace-code/esm-resolver';

import { Box, Stack, type SxProps } from '@mui/material';
import { useEffect, useId, useRef, useState } from 'react';

import { pluralize } from '@campaign-manager/core/lib/utils';

import Text from '../Text';

interface Props {
  value: string;
  onChange?: (value: string) => void;
  showCharCounter?: boolean;
  lengthLimit?: number;
  readOnly?: boolean;
  height?: string | number;
  sx?: SxProps;
  editorSx?: SxProps;
}

export default function AceEditor({
  value: initialValue,
  onChange,
  showCharCounter,
  lengthLimit,
  readOnly = false,
  height = 300,
  sx,
  editorSx,
}: Props) {
  const id = useId();
  const editorRef = useRef<ace.Ace.Editor | null>(null);

  const [count, setCount] = useState((initialValue ?? '').toString().length);

  useEffect(() => {
    const fn = () => {
      ace.config.set('basePath', 'path');
      editorRef.current = ace.edit(id, {
        mode: 'ace/mode/handlebars',
        theme: 'ace/theme/katzenmilch',
      });

      editorRef.current.setOptions({
        enableBasicAutocompletion: !readOnly,
        enableSnippets: !readOnly,
        enableLiveAutocompletion: false,
        fontSize: '14px',
        selectionStyle: 'text',
        autoScrollEditorIntoView: true,
        copyWithEmptySelection: true,
        readOnly,
        highlightActiveLine: !readOnly,
        highlightGutterLine: !readOnly,
      });

      editorRef.current.setValue(initialValue, -1);
      editorRef.current.session.on('change', () => {
        const nextValue = editorRef.current?.getValue() ?? '';
        // setValue(nextValue);
        onChange?.(nextValue);
        setCount(nextValue.length);
      });
    };

    fn();
  }, []);

  useEffect(() => {
    if (editorRef.current && initialValue !== editorRef.current.getValue()) {
      editorRef.current.setValue(initialValue, -1);
      setCount(initialValue.length);
    }
  }, [initialValue]);

  return (
    <Stack
      sx={[
        {
          gap: 0.5,
          alignItems: 'flex-start',
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box
        id={id}
        sx={{ width: '100%', height, ...editorSx }}
      />

      {showCharCounter && (
        <Text
          color="grey"
          size="xs"
          weight="medium"
        >
          {count}
          {lengthLimit ? `/${lengthLimit}` : ''} {pluralize(count, 'character')}
        </Text>
      )}
    </Stack>
  );
}
